import type { VRMCore } from '@pixiv/three-vrm-core'
import type { Ref } from 'vue'

import { MathUtils } from 'three'
import { ref } from 'vue'

/**
 * Available idle expressions for random selection
 * These are standard VRM expression names
 */
const IDLE_EXPRESSIONS = [
  'happy',
  'relaxed',
  'neutral',
] as const

/**
 * Less common expressions (lower probability)
 */
const RARE_EXPRESSIONS = [
  'surprised',
  'angry',
  'sad',
] as const

type IdleExpression = typeof IDLE_EXPRESSIONS[number] | typeof RARE_EXPRESSIONS[number]

interface IdleExpressionConfig {
  enabled: boolean
  // Min/max seconds between expression changes
  minInterval: number
  maxInterval: number
  // How long to hold an expression before returning to neutral
  holdDuration: number
  // Transition duration
  transitionDuration: number
}

const DEFAULT_CONFIG: IdleExpressionConfig = {
  enabled: true,
  minInterval: 8,
  maxInterval: 20,
  holdDuration: 3,
  transitionDuration: 0.5,
}

/**
 * Idle expression system for VRM models
 * Periodically triggers random expressions when idle
 */
export function useIdleExpressions(
  isSpeaking: Ref<boolean>,
  config: Ref<Partial<IdleExpressionConfig>> = ref({}),
) {
  let timeSinceLastExpression = 0
  let nextExpressionTime = MathUtils.randFloat(DEFAULT_CONFIG.minInterval, DEFAULT_CONFIG.maxInterval)
  let currentExpression: IdleExpression | null = null
  let expressionHoldTime = 0
  let isTransitioningOut = false

  // Track expression values for smooth transitions
  let currentValue = 0
  let targetValue = 0
  let transitionProgress = 0

  const activeExpression = ref<IdleExpression | null>(null)

  function getConfig(): IdleExpressionConfig {
    return { ...DEFAULT_CONFIG, ...config.value }
  }

  function pickRandomExpression(): IdleExpression {
    // 80% chance for common expressions, 20% for rare
    if (Math.random() < 0.8) {
      return IDLE_EXPRESSIONS[Math.floor(Math.random() * IDLE_EXPRESSIONS.length)]
    }
    return RARE_EXPRESSIONS[Math.floor(Math.random() * RARE_EXPRESSIONS.length)]
  }

  function easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2
  }

  function update(vrm: VRMCore | undefined, delta: number) {
    if (!vrm?.expressionManager)
      return

    const cfg = getConfig()
    if (!cfg.enabled)
      return

    // Don't trigger new expressions while speaking
    // But continue any ongoing transition
    if (!isSpeaking.value) {
      timeSinceLastExpression += delta
    }

    // Handle ongoing expression
    if (currentExpression) {
      // Transitioning in
      if (transitionProgress < 1 && !isTransitioningOut) {
        transitionProgress += delta / cfg.transitionDuration
        if (transitionProgress > 1)
          transitionProgress = 1

        currentValue = easeInOutCubic(transitionProgress) * targetValue
        vrm.expressionManager.setValue(currentExpression, currentValue)
      }
      // Holding
      else if (!isTransitioningOut) {
        expressionHoldTime += delta
        if (expressionHoldTime >= cfg.holdDuration) {
          isTransitioningOut = true
          transitionProgress = 0
        }
      }
      // Transitioning out
      else {
        transitionProgress += delta / cfg.transitionDuration
        if (transitionProgress > 1)
          transitionProgress = 1

        currentValue = (1 - easeInOutCubic(transitionProgress)) * targetValue
        vrm.expressionManager.setValue(currentExpression, currentValue)

        if (transitionProgress >= 1) {
          vrm.expressionManager.setValue(currentExpression, 0)
          currentExpression = null
          activeExpression.value = null
          isTransitioningOut = false
        }
      }
    }

    // Check if it's time for a new expression (only when idle and not transitioning)
    if (!isSpeaking.value && !currentExpression && timeSinceLastExpression >= nextExpressionTime) {
      const expression = pickRandomExpression()

      // Check if the VRM has this expression
      if (vrm.expressionManager.getValue(expression) !== null) {
        currentExpression = expression
        activeExpression.value = expression
        targetValue = MathUtils.randFloat(0.5, 1.0) // Random intensity
        currentValue = 0
        transitionProgress = 0
        expressionHoldTime = 0
        isTransitioningOut = false

        timeSinceLastExpression = 0
        nextExpressionTime = MathUtils.randFloat(cfg.minInterval, cfg.maxInterval)
      }
      else {
        // Expression not found, try again soon
        timeSinceLastExpression = 0
        nextExpressionTime = 2
      }
    }
  }

  function reset(vrm: VRMCore | undefined) {
    if (vrm?.expressionManager && currentExpression) {
      vrm.expressionManager.setValue(currentExpression, 0)
    }
    timeSinceLastExpression = 0
    nextExpressionTime = MathUtils.randFloat(DEFAULT_CONFIG.minInterval, DEFAULT_CONFIG.maxInterval)
    currentExpression = null
    activeExpression.value = null
    expressionHoldTime = 0
    isTransitioningOut = false
    currentValue = 0
    targetValue = 0
    transitionProgress = 0
  }

  /**
   * Force trigger a specific expression
   */
  function triggerExpression(vrm: VRMCore | undefined, expression: IdleExpression, intensity = 1.0) {
    if (!vrm?.expressionManager)
      return

    // Clear any existing expression
    if (currentExpression) {
      vrm.expressionManager.setValue(currentExpression, 0)
    }

    currentExpression = expression
    activeExpression.value = expression
    targetValue = intensity
    currentValue = 0
    transitionProgress = 0
    expressionHoldTime = 0
    isTransitioningOut = false
    timeSinceLastExpression = 0
  }

  return {
    update,
    reset,
    triggerExpression,
    activeExpression,
  }
}
